import moment from "moment";

import type {
  AiBriefing,
  AiBriefingItem,
  AiBriefingPerson,
} from "../../../../slices/AiSlice";

/** localStorage: { [userId]: { date: "YYYY-MM-DD", expanded } }; the next day starts collapsed. */
export const BRIEFING_STORAGE_KEY = "proc_home_briefing";

type Remembered = Record<string, { date: string; expanded: boolean }>;

export const localDay = (now: Date = new Date()): string =>
  moment(now).format("YYYY-MM-DD");

const readStore = (raw: string | null): Remembered => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Remembered)
      : {};
  } catch {
    return {};
  }
};

/** Expanded only if this person opened it today; anything else = collapsed. */
export const isExpandedToday = (
  raw: string | null,
  userId: string,
  today: string,
): boolean => {
  const entry = readStore(raw)[userId];
  return !!entry && entry.date === today && entry.expanded === true;
};

/** The new stored value: this person's choice for today; other people's entries kept, old days dropped. */
export const rememberExpanded = (
  raw: string | null,
  userId: string,
  today: string,
  expanded: boolean,
): string => {
  const store = readStore(raw);
  const next: Remembered = {};
  Object.entries(store).forEach(([id, entry]) => {
    if (entry && entry.date === today) next[id] = entry;
  });
  next[userId] = { date: today, expanded };
  return JSON.stringify(next);
};

export const readExpanded = (userId: string): boolean => {
  try {
    return isExpandedToday(
      localStorage.getItem(BRIEFING_STORAGE_KEY),
      userId,
      localDay(),
    );
  } catch {
    return false;
  }
};

export const saveExpanded = (userId: string, expanded: boolean) => {
  try {
    localStorage.setItem(
      BRIEFING_STORAGE_KEY,
      rememberExpanded(
        localStorage.getItem(BRIEFING_STORAGE_KEY),
        userId,
        localDay(),
        expanded,
      ),
    );
  } catch {
    // Remembering it is only a convenience.
  }
};

/** The three chips of the collapsed line; none when there is nothing at all. */
export const countChips = (counts: AiBriefing["counts"]): string[] => {
  const chips: string[] = [];
  if (counts.doFirst > 0) chips.push(`${counts.doFirst} to do first`);
  if (counts.waiting > 0)
    chips.push(
      `${counts.waiting} ${
        counts.waiting === 1 ? "client" : "clients"
      } to chase`,
    );
  if (counts.atRisk > 0) chips.push(`${counts.atRisk} at risk`);
  return chips;
};

/** "Your day, Friday Oct 31" (the server's day for the person). */
export const briefingHeading = (date: string): string => {
  const day = moment(date, "YYYY-MM-DD", true);
  return day.isValid() ? `Your day, ${day.format("dddd MMM D")}` : "Your day";
};

export const writtenLine = (briefing: AiBriefing): string => {
  const at = moment(briefing.generatedAt);
  const time = at.isValid() ? ` at ${at.format("h:mm A")}` : "";
  return briefing.aiWritten
    ? `Written by AI${time} from your work, emails and client portal. Nothing is sent or changed without you.`
    : `Put together${time} from your work, emails and client portal. Nothing is sent or changed without you.`;
};

/** Firm view: "Busiest this week: Joe Clover 8 due (3 overdue) · Sam Lee 5 due". */
export const busiestLine = (people: AiBriefingPerson[]): string | null => {
  const list = people.filter((p) => p.dueThisWeek > 0);
  if (!list.length) return null;
  return `Busiest this week: ${list
    .map(
      (p) =>
        `${p.name} ${p.dueThisWeek} due${
          p.overdue > 0 ? ` (${p.overdue} overdue)` : ""
        }`,
    )
    .join(" · ")}`;
};

/** What the Waiting on clients button does: write an email (compose window), else open the work item, else the client. */
export const waitingAction = (
  item: AiBriefingItem,
): { kind: "email" | "work" | "client" | "none"; label: string } => {
  if (item.email?.trim()) return { kind: "email", label: "Email" };
  if (item.workLink) return { kind: "work", label: "Open work" };
  if (item.link) return { kind: "client", label: "Open client" };
  return { kind: "none", label: "" };
};

/** Only in-app addresses are followed. */
export const isAppLink = (link?: string | null): link is string =>
  !!link && link.startsWith("/") && !link.startsWith("//");

export const ASK_SUGGESTION = "What should I do first today?";
